/* Component imports */
import { CardHighChartCP } from "modules/reports/components/charts/card-chart/CardHighChartCP";
import { FiltersNonConformityChartCP } from "modules/reports/components/filters/filter-non-conformity-chart/FiltersNonConformityChartCP";
import { HeaderCP } from "submodules/nerit-framework-ui/common/components/screen/layout/header/HeaderCP";
import { LayoutCP } from "submodules/nerit-framework-ui/common/components/screen/layout/layout/LayoutCP";
import { WelcomeContentWithFilterWrapperCP } from "app/components/welcome-content-with-filter-wrapper/WelcomeContentWithFilterWrapperCP";
/* Const imports */
import { SCREEN_FILTER_URL_PARAM } from "submodules/nerit-framework-ui/common/screens/inner/ScreenTypes";
/* Helper imports */
import { NotificationHelper } from "submodules/nerit-framework-ui/common/components/notification/inner/NotificationHelper";
/* Hook imports */
import { useFormStateManager } from "submodules/nerit-framework-ui/common/form-state-manager/UseFormStateManager";
import { useLocation } from "react-router";
import { useRequest } from "submodules/nerit-framework-ui/common/request-manager/use-request/UseRequest";
/* Model imports */
import { NonConformityChartFiltersFormModel } from "modules/reports/components/filters/filter-non-conformity-chart/inner/NonConformityChartFiltersFormModel";
/* React imports */
import React, { useEffect, useState } from "react";
/* Response imports */
import { NonConformityChartResponseDTO } from "submodules/nerit-aquatec-sdk/services/charts/dtos/responses/NonConformityChartResponseDTO";
/* Request imports */
import { ChartsRequests } from "submodules/nerit-aquatec-sdk/services/charts/ChartsRequests";
import { NonConformityChartRequestDTO } from "submodules/nerit-aquatec-sdk/services/charts/dtos/requests/NonConformityChartRequestDTO";
/* Util imports */
import { FormStateManagerUtils } from "submodules/nerit-framework-ui/common/form-state-manager/FormStateManagerUtils";
import { HighChartsUtils } from "modules/reports/components/charts/card-chart/inner/HighChartsUtils";
import {
  nonConformityExportSize,
  nonConformityScreenHeight,
} from "./inner/NonConformityChartSize";
import { RequestUtils } from "submodules/nerit-framework-utils/sdk-utils/request-manager/RequestUtils";
import { SystemUtils } from "submodules/nerit-framework-utils/utils/SystemUtils";
import { TableUtils } from "submodules/nerit-framework-ui/common/components/table/utils/TableUtils";
/* ScreenContentChartNonConformity */
export function ScreenContentChartNonConformity(): JSX.Element {
  const routeLocation = useLocation();

  const [formModel, setFormModel] =
    useState<NonConformityChartFiltersFormModel>(
      new NonConformityChartFiltersFormModel(),
    );
  const formStateManager = useFormStateManager(formModel);

  const [reload, setReload] = useState<number>();
  const [loadedData, setLoadedData] =
    useState<NonConformityChartResponseDTO[]>();
  const [chartWidth, setChartWidth] = useState(0);

  const [nonConformityChartRequestDTO, setNonConformityChartRequestDTO] =
    useState<NonConformityChartRequestDTO>();
  const getNonConformtyChartRequest =
    useRequest<NonConformityChartResponseDTO[]>();

  useEffect(onFetchNonConformityChartRequestChange, [
    getNonConformtyChartRequest.isAwaiting,
  ]);
  useEffect(init, []);
  useEffect(() => {
    filter();
  }, [reload]);

  function init(): void {
    const queryFilterStr = new URLSearchParams(routeLocation.search).get(
      SCREEN_FILTER_URL_PARAM,
    );

    let queryFilters: NonConformityChartRequestDTO | undefined;

    if (!!queryFilterStr) {
      queryFilters = JSON.parse(queryFilterStr);

      setFormModel(
        new NonConformityChartFiltersFormModel({
          dateRange: queryFilters?.dateRange,
          fieldRegulationCode: queryFilters?.regulationCode,
          locationCodes: queryFilters?.locationCodes,
        }),
      );

      SystemUtils.sleep(100).then(() => {
        setReload(TableUtils.getReloadNumber());
      });
    }
  }

  async function filter(): Promise<void> {
    if (
      !reload ||
      !(await FormStateManagerUtils.validateRequiredFields(formStateManager))
    ) {
      return;
    }

    setLoadedData(undefined);

    const formValues = formStateManager.getFormValues();

    const filters: NonConformityChartRequestDTO = {
      dateRange: formValues?.dateRange,
      locationCodes: formValues?.locationCodes,
      regulationCode: formValues?.fieldRegulationCode,
    };

    getNonConformtyChartRequest.runRequest(
      ChartsRequests.getNonConformityChart(filters),
    );

    setNonConformityChartRequestDTO(filters);
  }

  function onFetchNonConformityChartRequestChange(): void {
    if (
      !RequestUtils.isValidRequestReturn(
        getNonConformtyChartRequest,
        NotificationHelper.DEFAULT_ERROR_GET_MESSAGE,
      )
    ) {
      return;
    }

    setLoadedData(getNonConformtyChartRequest.responseData);
  }

  const exportSize = nonConformityExportSize(
    loadedData ? loadedData.length : 0,
  );

  return (
    <LayoutCP
      content={
        <>
          <FiltersNonConformityChartCP
            formStateManager={formStateManager}
            onFilter={() => setReload(TableUtils.getReloadNumber())}
          />

          <WelcomeContentWithFilterWrapperCP showWelcome={!loadedData}>
            <>
              {!!loadedData && !!nonConformityChartRequestDTO && (
                <>
                  <CardHighChartCP
                    alwaysShowLabels={true}
                    buttonsToHide={[
                      "chartType",
                      "fixedRatioSize",
                      "linearScale",
                      "showLabels",
                    ]}
                    chartType={"bar"}
                    resolveChartSize={(hostWidth) => {
                      setChartWidth((current) =>
                        current === hostWidth ? current : hostWidth,
                      );
                      const height = nonConformityScreenHeight(
                        hostWidth,
                        loadedData.length,
                      );
                      if (height === null) {
                        return null;
                      }
                      return { width: hostWidth, height };
                    }}
                    chartOptions={{
                      chart: nonConformityChartOptions(
                        chartWidth,
                        loadedData.length,
                      ),
                      exporting: {
                        sourceWidth: exportSize.width,
                        sourceHeight: exportSize.height,
                      },
                      series: [
                        {
                          data: loadedData.map((value) => ({
                            y: value.nonConformityPercent,
                          })),
                          name: "Percentual de Não Conformidades",
                          type: "bar",
                        },
                      ],
                      title: HighChartsUtils.getTitle(
                        "Percentual de Não Conformidades",
                      ),
                      xAxis: {
                        categories: loadedData.map((value) => value.fieldName),
                        tickWidth: 1,
                      },
                      yAxis: {
                        title: { text: "%" },
                      },
                    }}
                  />
                </>
              )}
            </>
          </WelcomeContentWithFilterWrapperCP>
        </>
      }
      header={<HeaderCP title={"Não Conformidades"} />}
      overflowVertical={true}
    />
  );
}

function nonConformityChartOptions(width: number, categoryCount: number) {
  const base = HighChartsUtils.getChart("bar");
  const height = nonConformityScreenHeight(width, categoryCount);
  if (height === null) {
    return { type: base.type };
  }
  return { ...base, width, height };
}
